import FormValidationClass from "../common/FormValidationClass";
import FormValidationMessagesClass from "../common/FormValidationMessagesClass";
import {
    fetchRequestSend, sendEventToAnalytics
} from "../../helpers/common-helpers";
import PreloaderClass from "../common/PreloaderClass";
import PostHogClass from "../common/PostHogClass";
import StyledCheckboxDefault from "../lms/StyledCheckboxDefault";

/**
 * Class representing the login functionality.
 */
export default class LoginFormClass {
    /**
     * Creates an instance of LoginClass.
     */
    constructor() {
        this.initializeEventListeners();
        this.initializeStyledCheckbox();
    }

    /**
     * Initializes event listeners for the login form.
     */
    initializeEventListeners() {
        // login form
        const loginForm = document.getElementById('login-form');
        if (loginForm) {
            loginForm.addEventListener('submit', e => this.handleFormSubmit(e, loginForm));
            sendEventToAnalytics('open_form_login');
        }

        // forgot password form
        const forgotPasswordForm = document.getElementById('forgot-password-form');
        if (forgotPasswordForm) {
            forgotPasswordForm.addEventListener('submit', e => this.handleFormSubmit(e, forgotPasswordForm));
        }

        // reset password form
        const resetPasswordForm = document.getElementById('reset-password-form');
        if (resetPasswordForm) {
            resetPasswordForm.addEventListener('submit', e => this.handleFormSubmit(e, resetPasswordForm));
        }
    }

    /**
     * Initialize styled checkbox for remember me
     */
    initializeStyledCheckbox() {
        // Initialize styled checkbox for the login form only
        const loginCheckbox = document.querySelector('#login-form .js-styled-checkbox');
        if (loginCheckbox) {
            new StyledCheckboxDefault('js-styled-checkbox');
        }
    }

    /**
     * Handles the form submission.
     * @param {Event} e - The submit event.
     * @param {HTMLFormElement} form - The form being submitted.
     */
    handleFormSubmit(e, form) {
        e.preventDefault();
        PreloaderClass.forPopup(form.id); // run PreloaderClass
        if (form.id === 'login-form') {
            this.login(form);
        } else if (form.id === 'forgot-password-form') {
            this.passwordReset(form);
        } else if (form.id === 'reset-password-form') {
            this.resetPassword(form);
        }
    }

    /**
     * Handles the login process.
     * @param {HTMLFormElement} form - The form being processed.
     */
    login(form) {
        const isFormValid = this.validateForm(form);

        if (isFormValid) {
            new FormValidationMessagesClass().hideErrorForForm(form.id);

            const formData = {
                email: form.querySelector('#login-email').value,
                password: form.querySelector('#login-password').value,
                remember_me: form.querySelector('#login-remember-me').checked ? 1 : 0,
            }

            this.sendLoginRequest(form.getAttribute('action'), formData)
                .then(response => {
                    PreloaderClass.stop(); // stop PreloaderClass
                    if (!response.login) {
                        new FormValidationMessagesClass().showErrorForForm('login-form', response.error, ['login-email', 'login-password']);
                    } else {
                        // PostHog: identify the now-known user first.
                        // With person_profiles: 'identified_only', identify() creates the person
                        // profile, so it must run BEFORE capture — otherwise the submit event is
                        // anonymous and may not stitch cleanly to the lead.
                        PostHogClass.identify(formData.email);

                        // sendBeacon so the event survives the redirect below.
                        sendEventToAnalytics('submit_form_login', {}, { transport: 'sendBeacon' });

                        window.location.href = response.redirect;
                    }
                });
        } else {
            PreloaderClass.stop(); // stop PreloaderClass
        }
    }

    /**
     * Handles the password reset process.
     * @param {HTMLFormElement} form - The form being processed.
     */
    passwordReset(form) {
        PreloaderClass.stop(); // stop PreloaderClass
        const isFormValid = this.validateForm(form);

        if (isFormValid) {
            new FormValidationMessagesClass().hideErrorForForm(form.id);
            const formData = {
                email: form.querySelector('#password-email').value,
            }

            this.sendLoginRequest(form.getAttribute('action'), formData)
                .then(response => {
                    PreloaderClass.stop(); // stop PreloaderClass
                    if (!response.status) {
                        new FormValidationMessagesClass().showErrorForForm('forgot-password-form', response.error, ['password-email']);
                    } else {
                        new FormValidationMessagesClass().showSuccessForForm('forgot-password-form', response.response);
                        setTimeout(() => {
                            //close popup
                            const popup = new vsPopupClass();
                            popup.close('forgot-password');
                        }, 2000);
                    }
                });
        } else {
            PreloaderClass.stop(); // stop PreloaderClass
        }
    }

    /**
     * Handles the reset password process.
     * @param {HTMLFormElement} form - The form being processed.
     */
    resetPassword(form) {
        const isFormValid = this.validateForm(form);

        if (isFormValid) {
            new FormValidationMessagesClass().hideErrorForForm(form.id);
            const formData = {
                password: form.querySelector('#newPassword').value,
                confirm_password: form.querySelector('#confirmPassword').value,
            }

            this.sendLoginRequest(form.getAttribute('action'), formData)
                .then(response => {
                    PreloaderClass.stop(); // stop PreloaderClass
                    if (!response.status) {
                        new FormValidationMessagesClass().showErrorForForm('reset-password-form', response.error, ['newPassword', 'confirmPassword']);
                    } else {
                        new FormValidationMessagesClass().showSuccessForForm('reset-password-form', response.success);
                        setTimeout(() => {
                            window.location.href = '/login';
                        }, 2000);
                    }
                });
        } else {
            PreloaderClass.stop(); // stop PreloaderClass
        }
    }

    /**
     * Validates the login form.
     * @returns {boolean} - True if the form is valid, false otherwise.
     */
    validateForm(form) {
        const formValidator = new FormValidationClass(form.id);
        return formValidator.init();
    }


    /**
     * Sends a login request to the server.
     * @param {string} action - The URL to send the request to.
     * @param {Object} data - The data to send in the request.
     * @returns {Promise<Object>} - The server response.
     */
    sendLoginRequest(action, data) {
        return fetchRequestSend(action, 'POST', data);
    }
}
